<template>
    <div data-testid="cambio-datos-paso-formulario">
        <!-- Error -->
        <div v-if="error" class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:mb-4">
            <p class="tw:m-0 tw:text-sm">{{ error }}</p>
        </div>

        <!-- Duplicate DNI error -->
        <div v-if="esDuplicado" class="tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg tw:mb-4">
            <p class="tw:m-0 tw:text-sm">{{ $t('perfil.edit_ticket.duplicate_dni') }}</p>
        </div>

        <ValidationObserver v-slot="{ invalid }">
            <!-- Entrada cards wrapper -->
            <CardFront custom-classes="tw:mb-6">
                <!-- Header: ticket count + event name -->
                <div class="tw:flex tw:justify-between tw:items-center tw:pb-3 tw:border-b tw:border-gray-200">
                    <span class="tw:font-bold tw:text-slate-800">
                        {{ $t('perfil.cambio_datos.tickets_seleccionados', { count: entradasSeleccionadas.length }) }}
                    </span>
                    <span v-if="pedido?.evento?.nombre" class="tw:text-sm tw:text-slate-500">
                        {{ pedido.evento.nombre }}
                    </span>
                </div>
                <div class="tw:space-y-4 tw:mt-4">
                    <CardFront
                        v-for="(entrada, index) in entradasSeleccionadas"
                        :key="entrada.id"
                        :data-testid="`cambio-datos-entrada-card-${entrada.id}`"
                        class="tw:gap-4"
                    >
                        <!-- Card header -->
                        <div class="tw:flex tw:items-center tw:gap-3 tw:border-slate-100">
                            <div class="tw:bg-slate-200 tw:w-8 tw:h-8 tw:rounded tw:flex tw:items-center tw:justify-center">
                                <i class="material-icons tw:text-slate-400 tw:text-2xl">confirmation_number</i>
                            </div>
                            <div class="tw:flex tw:flex-col">
                                <span class="tw:font-bold tw:text-slate-800">{{ $t('perfil.cambio_datos.entrada_index', { index: index + 1 }) }}</span>
                                <div class="tw:flex">
                                    <p class="tw:text-sm tw:text-slate-900 tw:m-0 tw:leading-tight">
                                        {{ entrada.user_nombre }}  
                                        <span>{{ entrada.user_apellidos }}</span>
                                        <span v-if="entrada.user_dni " class="tw:text-slate-800 tw:mx-1">&bull;</span>
                                    </p>
                                    <p class="tw:text-xs tw:text-slate-500 tw:font-mono tw:m-0">{{ entrada.user_dni }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Form grid -->
                        <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-4 tw:gap-3">
                            <InputText
                                :label="$t('formularios.nombre')"
                                :data-testid="`cambio-datos-input-nombre-${entrada.id}`"
                                name="nombre"
                                rules="required"
                                :value="datosNuevos[entrada.id]?.nombre"
                                @input="emit('update-dato', { ticketId: entrada.id, campo: 'nombre', valor: $event })"
                            />
                            <InputText
                                :label="$t('formularios.apellidos')"
                                :data-testid="`cambio-datos-input-apellidos-${entrada.id}`"
                                name="apellidos"
                                rules="required"
                                :value="datosNuevos[entrada.id]?.apellidos"
                                @input="emit('update-dato', { ticketId: entrada.id, campo: 'apellidos', valor: $event })"
                            />
                            <div class="tw:flex tw:flex-col tw:md:flex-row tw:items-start tw:gap-2">
                                <div class="tw:w-full tw:shrink-0">
                                    <DocumentType
                                        :value="datosNuevos[entrada.id]?.tipo_documento || ''"
                                        :index="index"
                                        @input="emit('update-dato', { ticketId: entrada.id, campo: 'tipo_documento', valor: $event })"
                                    />
                                </div>
                                <div class="tw:w-full tw:shrink-0">
                                    <DocumentValue
                                        :data-testid="`cambio-datos-input-dni-${entrada.id}`"
                                        :value="datosNuevos[entrada.id]?.dni || ''"
                                        :type="datosNuevos[entrada.id]?.tipo_documento || null"
                                        :index="index"
                                        @input="emit('update-dato', { ticketId: entrada.id, campo: 'dni', valor: $event })"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardFront>
                </div>
            </CardFront>

            <!-- Summary card -->
            <CardFront custom-classes="tw:mb-6">
                <div data-testid="cambio-datos-resumen-total" class="tw:flex tw:justify-between tw:items-center tw:text-sm tw:text-slate-600">
                    <span>{{ entradasSeleccionadas.length }} {{ entradasSeleccionadas.length === 1 ? (terminoEntrada || $t('perfil.entrada')) : (terminoEntradas || $t('perfil.entradas')) }} × {{ formatearPrecio(costePorCambio) }}</span>
                    <span>{{ formatearPrecio(totalCambios) }}</span>
                </div>
                <div class="tw:flex tw:justify-between tw:items-center  tw:pt-2 tw:border-t tw:border-slate-100 tw:mt-2">
                    <span class="tw:font-bold tw:text-xl tw:text-slate-900">{{ $t('perfil.cambio_datos.total_a_pagar') }}</span>
                    <span class="tw:font-bold tw:text-xl tw:text-red-500">{{ formatearPrecio(totalCambios) }}</span>
                </div>
            </CardFront>

            <!-- Términos y condiciones -->
            <CardFront custom-classes="tw:mb-6">
                <FormTerminos
                    :valor="terminos"
                    :mostrar-error="terminosTocados"
                    @update:valor="onTerminosChange"
                />
            </CardFront>

            <!-- Footer actions -->
            <div class="tw:flex tw:justify-between tw:items-center tw:gap-3">
                <Button variant="default" data-testid="cambio-datos-btn-volver-form" @click="emit('volver')">
                    <i class="material-icons tw:text-sm">arrow_back</i>
                    {{ $t('perfil.cambio_datos.cancelar_volver') }}
                </Button>
                <Button
                    variant="alt"
                    data-testid="cambio-datos-btn-continuar-pago"
                    :disabled="invalid || loading || !terminosValidos || !isFormValid || esDuplicado"
                    :loading="loading"
                    @click="emit('continuar')"
                >
                    <span v-if="!loading">{{ $t('perfil.cambio_datos.continuar_pago') }}</span>
                    <i v-if="!loading" class="material-icons tw:text-sm">arrow_forward</i>
                </Button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import { useFilters } from '@/composables/front/formatters/useFilters';
    import { ValidationObserver } from 'vee-validate';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import DocumentType from '@/modules/front/ui/DocumentType.vue';
    import DocumentValue from '@/modules/front/ui/DocumentValue.vue';
    import FormTerminos from '@/modules/front/ui/FormTerminos.vue';

    const { formatearPrecio } = useFilters();

    const terminos = ref({ privacidad: false, condiciones: false, comunicaciones: false, plazos: false });
    const terminosTocados = ref(false);
    const terminosValidos = computed(() => terminos.value.privacidad && terminos.value.condiciones);
    const onTerminosChange = val => { terminos.value = val; terminosTocados.value = true; };

    defineProps({
        entradasSeleccionadas: { type: Array, required: true },
        datosNuevos:           { type: Object, required: true },
        costePorCambio:        { type: Number, required: true },
        totalCambios:          { type: Number, required: true },
        loading:               { type: Boolean, default: false },
        error:                 { type: String, default: null },
        isFormValid:           { type: Boolean, default: false },
        esDuplicado:           { type: Boolean, default: false },
        terminoEntrada:        { type: String, default: '' },
        terminoEntradas:       { type: String, default: '' },
        pedido:                { type: Object, default: null },
        teatro:                { type: Object, default: null },
    });

    const emit = defineEmits(['update-dato', 'volver', 'continuar']);
</script>
